import React, { useEffect, useRef, useState } from 'react';
import { FaPlay, FaSpinner, FaVideo } from 'react-icons/fa';

import { fetchContentById } from '@/services/contentService';
import { appSettings } from '@/settings/app-settings';
import { VideoContent } from '@/types/content';
import { VideoPlayer as LegacyVideoPlayer } from '@/ux-legacy/components/common/VideoPlayer/VideoPlayer';

interface VideoGridPreviewProps {
  contentId?: string;
  src?: string;
  title?: string;
  className?: string;
}

export const VideoGridPreview: React.FC<VideoGridPreviewProps> = ({
  contentId,
  src,
  title,
  className = '',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videoData, setVideoData] = useState<VideoContent | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const loadVideoData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (contentId) {
          const content = await fetchContentById(contentId);

          if (content.type !== 'video') {
            throw new Error('Content is not a video');
          }

          const video = content as VideoContent;
          setVideoData(video);

          // Construct video URL
          const url = video.url;
          if (url) {
            if (url.startsWith('http://') || url.startsWith('https://')) {
              setVideoUrl(url);
            } else if (url.startsWith('/')) {
              setVideoUrl(`${appSettings.api.VIDEO_URL}${url}`);
            } else {
              const baseUrl = appSettings.api.VIDEO_URL || '';
              const separator = baseUrl.endsWith('/') ? '' : '/';
              setVideoUrl(`${baseUrl}${separator}${url}`);
            }
          }
        } else if (src) {
          // Use src directly if no contentId
          setVideoUrl(getVideoUrl(src));
        }
      } catch (err) {
        console.error('Error loading video:', err);
        setError('Failed to load video content');
      } finally {
        setIsLoading(false);
      }
    };

    loadVideoData();
  }, [contentId, src]);

  const getVideoUrl = (url: string) => {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
      return url;
    }

    const baseUrl = appSettings.api.VIDEO_URL || '';
    if (url.startsWith('/')) {
      return `${baseUrl}${url}`;
    }

    const separator = baseUrl.endsWith('/') ? '' : '/';
    return `${baseUrl}${separator}${url}`;
  };

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  const displayTitle = videoData?.title || title || 'Video Content';

  return (
    <div className={`video-grid-preview w-full h-full ${className}`}>
      <div className="relative bg-black overflow-hidden w-full h-full">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <FaSpinner className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-red-900/20 p-4">
            <div className="text-center">
              <FaVideo className="h-12 w-12 text-red-400 mx-auto mb-2" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        ) : (
          <>
            {isPlaying && videoUrl ? (
              <LegacyVideoPlayer
                ref={videoRef}
                className="w-full h-full"
                source={videoUrl}
                controls={true}
                autoPlay={true}
                muted={false}
                playsInline={true}
              />
            ) : (
              <>
                {/* Thumbnail or placeholder */}
                {videoData?.metadata?.thumbnailUrl ? (
                  <img
                    src={videoData.metadata.thumbnailUrl}
                    alt={displayTitle}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                    <FaVideo className="h-16 w-16 text-gray-600" />
                  </div>
                )}

                {/* Play button overlay */}
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer hover:bg-black/20 transition-colors"
                  onClick={handlePlayPause}
                >
                  <div className="bg-black/60 hover:bg-black/80 p-4 rounded-full transition-all duration-200 hover:scale-110">
                    <FaPlay className="h-10 w-10 text-white ml-1" />
                  </div>
                </div>

                {/* Title overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <h3 className="text-white font-semibold text-lg">{displayTitle}</h3>
                  {videoData?.metadata?.duration && (
                    <p className="text-gray-300 text-sm">{formatDuration(videoData.metadata.duration)}</p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
